import { useCallback, useEffect } from 'react';
import { ChatbotProvider, useChatbot } from '@fff-web/chatbot';
import { useUserContext } from '@fff-web/fff-utilities';
import isBrowser from '../../utils/isBrowser';
import { type ChatWidgetProps, type DefaultChatWidgetProps } from './ChatWidget.types';
import { fetchChatbotMetadata, flattenUserInfo, isChatPathAllowed } from './ChatWidget.utils';

const ChatWidgetDefault = ({ hideWidget }: DefaultChatWidgetProps) => {
  // State
  const { userInfo } = useUserContext();
  const { isMounted, isOpen, mount, unmount, updateMetadata } = useChatbot();

  // Helpers
  const isPathAllowed = isChatPathAllowed();

  // Effects
  /**
   * Manages chatbot mounting state
   */
  useEffect(() => {
    const shouldMount = isBrowser() && isPathAllowed && !hideWidget;

    if (!shouldMount) {
      isMounted && unmount();
      return;
    }

    if (!isMounted) {
      mount({
        userId: userInfo.user.userId?.toString(),
        metadata: { context: flattenUserInfo(userInfo.user) },
      });
    }
  }, [isMounted, hideWidget, mount, unmount, userInfo.user, isPathAllowed]);

  /**
   * Handle chat open event - ALWAYS fetch fresh metadata on every open
   */
  const handleChatOpen = useCallback(async () => {
    const context = await fetchChatbotMetadata();
    updateMetadata({ context });
  }, [updateMetadata]);

  /**
   * Subscribe to chat open event
   */
  useEffect(() => {
    if (!isMounted || !isOpen) return;
    handleChatOpen();
  }, [isMounted, isOpen, handleChatOpen]);

  return null;
};

export const ChatWidget = ({ agentId, ...props }: ChatWidgetProps) => {
  return (
    <ChatbotProvider agentId={agentId}>
      <ChatWidgetDefault {...props} />
    </ChatbotProvider>
  );
};
